import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { findDefinitionUses, DefinitionUse } from '../../utils/definitionLink';
import { showAlert } from '../../../common/AppDialog';
import { logError, logUsage } from '../../../../services/eventLog';
import { IS_LOCAL } from '../../../../config/mode';

/**
 * 정의가 바뀐 뒤 부른다 — 그 정의를 쓰는 워크플로를 알린다.
 *
 * 데이터 노드는 정의 id 만 저장하고 세부 값은 쓸 때 정의에서 읽는다(utils/definitionRefs, 서버 definition_refs).
 * 그래서 예전처럼 워크플로마다 새 버전을 저장해 값을 퍼뜨릴 일이 없다 — 열린 캔버스는 정의 목록을 새로 받으면
 * 저절로 다시 채워지고(App → hydrateLinkedNodes), 저장된 워크플로는 다음에 열거나 돌릴 때 정의를 읽는다.
 * 남는 일은 **이미 배포해 둔 DAG** 다 — 배포 순간의 값이 들어 있으므로 다시 배포해야 한다(Airflow 판).
 *
 * 부르기 전에 정의 목록을 새로 받아 두어야 한다(fetchDataDefinitions). 쓰는 곳이 없으면 조용히 끝낸다.
 */
export function useDefinitionPropagation() {
  const { t } = useTranslation();

  return useCallback(async (defIds: string[]) => {
    if (!defIds.length) return;
    const started = Date.now();
    try {
      const byWorkflow = new Map<string, DefinitionUse>();
      const failed: Array<{ name: string; error: string }> = [];
      for (const id of defIds) {
        const r = await findDefinitionUses(id);
        r.uses.forEach((u) => {
          const prev = byWorkflow.get(u.workflowId);
          byWorkflow.set(u.workflowId, prev ? { ...prev, nodes: prev.nodes + u.nodes } : u);
        });
        failed.push(...r.failed);
      }
      const uses = Array.from(byWorkflow.values());
      logUsage('datadef.save', { detail: { defs: defIds.length, usedBy: uses.length, failed: failed.length }, durationMs: Date.now() - started });
      if (!uses.length) return;
      const lines = [
        t('datadef:link.usedBy', { n: uses.length }),
        ...uses.map((u) => `  · ${u.name} — ${t('datadef:link.nodes', { n: u.nodes })}`),
      ];
      // 무설치판은 저장된 캔버스로 돌며 그때 정의를 읽는다. Airflow 판은 배포된 DAG 가 돈다 — 다시 배포해야 한다
      if (!IS_LOCAL) lines.push('', t('datadef:link.redeploy'));
      showAlert(lines.join('\n'), { title: t('datadef:link.title'), tone: 'success' });
    } catch (e) {
      logError('datadef.save', e, { where: 'uses' });
      showAlert(t('datadef:link.error', { message: (e as Error).message }), { tone: 'warning' });
    }
  }, [t]);
}
